import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { X, Wifi, Shield, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface WalletConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (walletType: string) => void;
}

const wallets = [
  {
    id: 'foundation',
    name: 'Vatton Foundation',
    icon: '🏛️',
    description: 'Official foundation wallet',
    color: 'from-green-500 to-emerald-600',
    isFoundation: true
  },
  {
    id: 'metamask',
    name: 'MetaMask',
    icon: '🦊',
    description: 'Popular Web3 wallet',
    color: 'from-orange-500 to-yellow-500'
  },
  {
    id: 'trustwallet',
    name: 'Trust Wallet',
    icon: '🛡️',
    description: 'Secure mobile wallet',
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'walletconnect',
    name: 'WalletConnect',
    icon: '🔗',
    description: 'Connect any wallet',
    color: 'from-purple-500 to-purple-600'
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    icon: '💙',
    description: 'Self-custody wallet',
    color: 'from-blue-600 to-indigo-600'
  }
];

export function WalletConnectModal({ isOpen, onClose, onConnect }: WalletConnectModalProps) {
  const [connecting, setConnecting] = useState(null as string | null);

  const handleConnect = async (walletId: string, walletName: string) => {
    // 지갑 연결 비활성화 - 준비 중 (조용히 처리)
    console.log('지갑 연결 기능은 준비 중입니다.');
    return;
    
    setConnecting(walletId);
    
    try {
      if (walletId === 'foundation') {
        // 재단지갑 연결 로직
        const foundationWallet = '0xVATTONFOUNDATION000000000000000A888888';
        
        // 재단지갑 정보 조회
        const response = await fetch(`http://159.223.63.184:3601/api/vatton/wallet/balance/${foundationWallet}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // 재단지갑 연결 성공
            localStorage.setItem('vatton-wallet-address', foundationWallet);
            localStorage.setItem('vatton-wallet-type', 'foundation');
            localStorage.setItem('vatton-foundation-connected', 'true');
            
            onConnect('Foundation Wallet');
            toast.success(`🏛️ ${walletName} 연결 완료!`);
            onClose();
            return;
          }
        }
        throw new Error('재단지갑 연결 실패');
      } else {
        // 일반 지갑 연결 로직
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Mock successful connection
        onConnect(walletName);
        toast.success(`🎉 ${walletName} connected successfully!`);
        onClose();
      }
    } catch (error) {
      toast.error(`Failed to connect ${walletName}`);
    } finally {
      setConnecting(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-background border-border w-[95vw] max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-bold text-foreground">
            Connect to Web3 Wallet
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            Choose your preferred wallet to connect
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-6">
          {wallets.map((wallet) => (
            <button
              key={wallet.id}
              onClick={() => handleConnect(wallet.id, wallet.name)}
              disabled={true}
              className={`w-full p-4 border rounded-2xl transition-all duration-200 opacity-50 cursor-not-allowed group ${
                wallet.isFoundation 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700' 
                  : 'bg-card border-border'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 bg-gradient-to-r ${wallet.color} rounded-2xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-105 transition-transform ${
                  wallet.isFoundation ? 'ring-2 ring-green-300 dark:ring-green-600' : ''
                }`}>
                  {wallet.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`font-bold ${wallet.isFoundation ? 'text-green-700 dark:text-green-300' : 'text-foreground'}`}>
                    {wallet.name}
                    {wallet.isFoundation && <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">공식</span>}
                  </h3>
                  <p className={`text-sm ${wallet.isFoundation ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                    {wallet.description}
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                    준비 중...
                  </p>
                </div>
                {connecting === wallet.id ? (
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <div className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors">
                    →
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* App Permissions */}
        <div className="mt-6 p-4 bg-muted rounded-2xl">
          <h4 className="font-bold text-foreground mb-3 flex items-center">
            <Shield className="w-4 h-4 mr-2 text-blue-600" />
            App Permissions
          </h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm text-muted-foreground">View your wallet balance and activity</span>
            </div>
            <div className="flex items-center space-x-3">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm text-muted-foreground">Request approval for transactions</span>
            </div>
          </div>
        </div>

        {/* Networks */}
        <div className="mt-4 p-4 bg-muted rounded-2xl">
          <h4 className="font-bold text-foreground mb-3 flex items-center">
            <Wifi className="w-4 h-4 mr-2 text-purple-600" />
            Supported Networks
          </h4>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">E</span>
            </div>
            <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">P</span>
            </div>
            <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">B</span>
            </div>
            <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-green-600 rounded-full">
              <span className="text-white text-xs font-bold flex items-center justify-center w-full h-full">A</span>
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={onClose}
          className="w-full mt-4 border-border text-muted-foreground hover:bg-accent"
        >
          Cancel Connection
        </Button>
      </DialogContent>
    </Dialog>
  );
}